import { languageConfigType } from './language.types';

export const SELECTED_LANGUAGE_STORAGE_KEY = 'language.selected';

// 'en-FORMAL' / 'en-GB' → 'en': app codes carry a form-of-address suffix, browser codes a region.
const primaryLanguage = (code: string) => code.split(/[-_]/)[0].toLowerCase();

/**
 * Start language, in this order (#342):
 * 1. the user's stored manual choice, if the tenant still offers it,
 * 2. the first browser language the tenant offers — the default language wins when it speaks
 *    that language, so the tenant's form of address (formal/informal) is kept,
 * 3. the configured default language.
 */
export const resolveStartLanguage = (config: languageConfigType, browserLanguages: readonly string[], storedLanguage?: string): string => {
    const enabled = config.enabledLanguages ?? [];

    if (storedLanguage) {
        const stored = enabled.find((language) => language.name.toLowerCase() === storedLanguage.toLowerCase());
        if (stored) {
            return stored.name;
        }
    }

    for (const browserLanguage of browserLanguages) {
        const primary = primaryLanguage(browserLanguage);
        if (primaryLanguage(config.defaultLanguage) === primary) {
            return config.defaultLanguage;
        }
        const match = enabled.find((language) => primaryLanguage(language.name) === primary);
        if (match) {
            return match.name;
        }
    }

    return config.defaultLanguage;
};

export const readBrowserLanguages = (): readonly string[] => {
    if (typeof navigator === 'undefined') {
        return [];
    }
    if (navigator.languages?.length) {
        return navigator.languages;
    }
    return navigator.language ? [navigator.language] : [];
};

export const readSelectedLanguage = (): string | undefined => {
    try {
        return localStorage.getItem(SELECTED_LANGUAGE_STORAGE_KEY) ?? undefined;
    } catch {
        return undefined;
    }
};

export const storeSelectedLanguage = (language: string) => {
    try {
        localStorage.setItem(SELECTED_LANGUAGE_STORAGE_KEY, language);
    } catch {
        // Storage unavailable (private mode) — the choice only applies for this session
    }
};
